import React, { useState, useEffect } from 'react';
import { getVillageByID, createVHND, getBeneficiaries } from '../services/authService';
import { useSelector } from 'react-redux';
import '.././styles/reports/vhndform.css';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../utils/alert';
import Loader from '../components/Loader';
// Initial data with empty beneficiaries
const initialData = {
  date: '2023-08-15',
  time: '10:00',
  location: 'Rampur Primary School',
  focusArea: 'Maternal & Child Health',
  staff: [
    { name: 'Dr. Mehta', role: 'Medical Officer', status: 'Confirmed' },
    { name: 'Smt. Gupta', role: 'ANM', status: 'Confirmed' },
    { name: 'Smt. Sharma', role: 'Anganwadi Worker', status: 'Confirmed' },
    { name: 'Shri Kumar', role: 'Multi-purpose Worker', status: 'Pending' },
  ],
  beneficiaries: [], // Empty initially, will be populated by API
  services: [
    { title: 'Antenatal Care', description: 'ANC check-ups, IFA distribution', selected: true },
    { title: 'Immunization', description: 'Routine vaccines for children & mothers', selected: true },
    { title: 'Growth Monitoring', description: 'Weight, height, MUAC measurement', selected: true },
    { title: 'Family Planning', description: 'Counseling, contraceptive distribution', selected: false },
    { title: 'Geriatric Care', description: 'BP check, diabetes screening', selected: true },
    { title: 'General Health', description: 'Basic health check-ups, consultations', selected: false },
  ],
  logistics: [
    { title: 'Vaccines & Cold Chain', description: 'Routine vaccines, ice packs, vaccine carriers', checked: true },
    { title: 'Weighing Scales', description: 'Infant weighing scale, adult weighing scale', checked: true },
    { title: 'Medicines & Supplements', description: 'IFA tablets, calcium, ORS, zinc, deworming tablets', checked: true },
    { title: 'Equipment & Instruments', description: 'BP apparatus, stethoscope, thermometer, glucometer', checked: false },
    { title: 'Records & Registers', description: 'VHND register, immunization cards, growth charts', checked: true },
    { title: 'IEC Materials', description: 'Posters, flip charts, models for demonstrations', checked: true },
  ],
  outreach: {
    villageAnnouncement: true,
    smsReminders: true,
    panchayatInformed: true,
    bannersPosters: true,
    additionalNotes: 'Home visits planned for elderly who cannot travel to the venue. School principal informed to announce to parents. Local SHGs will help in mobilizing pregnant women and children.',
  },
  notes: 'Special focus on maternal nutrition this month. Dr. Mehta will give a short talk on importance of institutional delivery. Need to arrange for shade/shelter as forecast shows possibility of rain.',
};

const VhndCreate = () => {
  // State for form data
  const [formData, setFormData] = useState(initialData);
  const [villageData, setVillageData] = useState({});
  const [loadingVillage, setLoadingVillage] = useState(true);
  const [villageError, setVillageError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [beneficiaryFilter, setBeneficiaryFilter] = useState('All Categories');
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '', role: '', status: 'Pending' });
  const [loadingBeneficiaries, setLoadingBeneficiaries] = useState(true);
  const [beneficiariesError, setBeneficiariesError] = useState(null);
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const navigate = useNavigate();
  // Fetch village and beneficiaries data
  useEffect(() => {
    const fetchVillageData = async () => {
      try {
        const villageId = localStorage.getItem('VillageId');
        const response = await getVillageByID(villageId);
        if (response.status && response.data) {
          setVillageData(response.data);
        } else {
          setVillageError(response.message || 'Failed to fetch village data');
        }
      } catch (err) {
        setVillageError(`Error fetching village data: ${err.message}`);
        console.error('API error:', err);
      } finally {
        setLoadingVillage(false);
      }
    };

    const fetchBeneficiariesData = async () => {
      try {
        const villageId = localStorage.getItem('VillageId');
        const response = await getBeneficiaries(villageId);
        if (response.status && response.data) {
          // Map API data to the expected format, including beneficiary_id
          const formattedBeneficiaries = response.data.map((b) => ({
            beneficiary_id: b.beneficiary_id, // Ensure API returns beneficiary_id
            name: b.name,
            category: b.category,
            age: b.age,
            details: b.dueServices ? `${b.age} years • Due services: ${b.dueServices}` : `${b.age} years • No due services`,
            selected: true,
          }));
          setFormData((prev) => ({
            ...prev,
            beneficiaries: formattedBeneficiaries,
          }));
        } else {
          setBeneficiariesError(response.message || 'Failed to fetch beneficiaries data');
        }
      } catch (err) {
        setBeneficiariesError(`Error fetching beneficiaries data: ${err.message}`);
        console.error('API error:', err);
      } finally {
        setLoadingBeneficiaries(false);
      }
    };

    fetchVillageData();
    fetchBeneficiariesData();
  }, [selectedVillageId]);

  // Handle input changes
  const handleInputChange = (e, section, index = null) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      if (section === 'root') {
        return { ...prev, [name]: value };
      } else if (section === 'staff' && index !== null) {
        const updatedStaff = [...prev.staff];
        updatedStaff[index] = { ...updatedStaff[index], [name]: value };
        return { ...prev, staff: updatedStaff };
      } else if (section === 'beneficiaries' && index !== null) {
        const updatedBeneficiaries = [...prev.beneficiaries];
        updatedBeneficiaries[index] = { ...updatedBeneficiaries[index], selected: checked };
        return { ...prev, beneficiaries: updatedBeneficiaries };
      } else if (section === 'services' && index !== null) {
        const updatedServices = [...prev.services];
        updatedServices[index] = { ...updatedServices[index], selected: checked };
        return { ...prev, services: updatedServices };
      } else if (section === 'logistics' && index !== null) {
        const updatedLogistics = [...prev.logistics];
        updatedLogistics[index] = { ...updatedLogistics[index], checked: checked };
        return { ...prev, logistics: updatedLogistics };
      } else if (section === 'outreach') {
        return { ...prev, outreach: { ...prev.outreach, [name]: type === 'checkbox' ? checked : value } };
      }
      return prev;
    });
  };

  // Handle new staff input
  const handleNewStaffChange = (e) => {
    const { name, value } = e.target;
    setNewStaff((prev) => ({ ...prev, [name]: value }));
  };

  // Add new staff member
  const addStaffMember = () => {
    if (!newStaff.name || !newStaff.role) {
      alert('Please provide name and role for the staff member.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      staff: [...prev.staff, newStaff],
    }));
    setNewStaff({ name: '', role: '', status: 'Pending' });
    setShowAddStaff(false);
  };

  // Select all beneficiaries
  const selectAllBeneficiaries = () => {
    setFormData((prev) => ({
      ...prev,
      beneficiaries: prev.beneficiaries.map((b) => ({ ...b, selected: true })),
    }));
  };

  // Filter beneficiaries
  const filteredBeneficiaries = formData.beneficiaries.filter(
    (b) =>
      (beneficiaryFilter === 'All Categories' || b.category === beneficiaryFilter) &&
      b.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const participation = async () => {
  navigate('/vhnd-participation');

  }
  // Submit form data to API
  const handleSubmit = async () => {
    if (!formData.date || !formData.time || !formData.location || !formData.focusArea) {
      alert('Please fill in all required fields (Date, Time, Location, Focus Area).');
      return;
    }
    setLoading(true);
    setError(null); // Reset error state
    try {
      const response = await createVHND(formData);
      if (response.status) {
        showSuccess('VHND Plan submitted successfully!');
        navigate('/vhnd');
        console.log('API Response:', response);
        // Optionally reset form
        setFormData(initialData);
      } else {
        throw new Error(response.message || 'Failed to submit VHND plan');
      }
    } catch (error) {
      console.error('Error submitting VHND plan:', error);
      setError(error.message);
      showError(`Error submitting VHND Plan: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Combined loading and error states
  if (loadingVillage || loadingBeneficiaries) return <div>Loading...</div>;
  if (villageError || beneficiariesError) return <div>Error: {villageError || beneficiariesError}</div>;

  return (
    <>
      <div className="page-header">
        <button className="back-button">
          <span className="material-icons">arrow_back</span>
        </button>
        <div className="page-title">VHND Planning</div>
      </div>

      <div className="form-card">
        <div className="tab-container">
          <div className="tab active">Plan VHND</div>
          <div className="tab" onClick={participation}>Record Participation</div>

        </div>

        {/* VHND Basic Info */}
        <div className="form-section">
          <div className="section-title">Basic Information</div>

          <div className="village-card">
            <div className="village-header">
              <div className="village-name">{villageData?.village_name} Village</div>
            </div>
            <div className="village-details">
              Population: {villageData?.population.toLocaleString()} • Households:{' '}
              {villageData?.households.toLocaleString()}
            </div>
            <div className="village-details">
              Block: {villageData?.block} • Panchayat: {villageData?.panchayat}
            </div>
            <div className="village-details">
              District: {villageData?.district} • State: {villageData?.state}
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="label">Date</label>
              <input
                type="date"
                className="date-picker"
                name="date"
                value={formData.date}
                onChange={(e) => handleInputChange(e, 'root')}
              />
            </div>
            <div className="form-field">
              <label className="label">Time</label>
              <input
                type="time"
                className="input-field"
                name="time"
                value={formData.time}
                onChange={(e) => handleInputChange(e, 'root')}
              />
            </div>
          </div>

          <div className="form-field">
            <label className="label">Location</label>
            <select
              className="input-field"
              name="location"
              value={formData.location}
              onChange={(e) => handleInputChange(e, 'root')}
            >
              <option value="">Select Location</option>
              <option>Anganwadi Center No. 1</option>
              <option>Rampur Primary School</option>
              <option>Panchayat Bhawan</option>
              <option>PHC Sub-Center</option>
              <option>Other</option>
            </select>
          </div>

          <div className="form-field">
            <label className="label">Special Focus Area (if any)</label>
            <select
              className="input-field"
              name="focusArea"
              value={formData.focusArea}
              onChange={(e) => handleInputChange(e, 'root')}
            >
              <option value="">Select Focus Area</option>
              <option>Regular VHND</option>
              <option>Maternal & Child Health</option>
              <option>Immunization</option>
              <option>Nutrition</option>
              <option>Adolescent Health</option>
              <option>Geriatric Health</option>
              <option>Disease Prevention</option>
            </select>
          </div>
        </div>

        <div className="divider"></div>

        {/* Healthcare Staff */}
        <div className="form-section">
          <div className="section-title">Healthcare Staff</div>
          <div className="section-subtitle">Confirm staff attendance for VHND</div>

          <div className="staff-cards">
            {formData.staff.map((staff, index) => (
              <div className="staff-card" key={index}>
                <div className="staff-avatar">
                  <span className="material-icons">
                    {staff.role === 'Medical Officer'
                      ? 'local_hospital'
                      : staff.role === 'ANM'
                      ? 'health_and_safety'
                      : staff.role === 'Anganwadi Worker'
                      ? 'child_care'
                      : 'person'}
                  </span>
                </div>
                <div className="staff-name">{staff.name}</div>
                <div className="staff-role">{staff.role}</div>
                <div
                  className={`staff-status ${
                    staff.status === 'Confirmed'
                      ? 'status-confirmed'
                      : staff.status === 'Pending'
                      ? 'status-pending'
                      : 'status-unavailable'
                  }`}
                >
                  {staff.status}
                </div>
              </div>
            ))}
          </div>

          <button
            className="select-button"
            style={{ width: 'auto', borderColor: '#795548', color: '#795548' }}
            onClick={() => setShowAddStaff(true)}
          >
            <span className="material-icons" style={{ marginRight: '8px' }}>
              person_add
            </span>
            Add Staff Member
          </button>

          {showAddStaff && (
            <div className="form-section">
              <div className="form-row">
                <div className="form-field">
                  <label className="label">Name</label>
                  <input
                    type="text"
                    className="input-field"
                    name="name"
                    value={newStaff.name}
                    onChange={handleNewStaffChange}
                  />
                </div>
                <div className="form-field">
                  <label className="label">Role</label>
                  <select
                    className="input-field"
                    name="role"
                    value={newStaff.role}
                    onChange={handleNewStaffChange}
                  >
                    <option value="">Select Role</option>
                    <option>Medical Officer</option>
                    <option>ANM</option>
                    <option>Anganwadi Worker</option>
                    <option>Multi-purpose Worker</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="label">Status</label>
                  <select
                    className="input-field"
                    name="status"
                    value={newStaff.status}
                    onChange={handleNewStaffChange}
                  >
                    <option>Pending</option>
                    <option>Confirmed</option>
                    <option>Unavailable</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <button
                  className="select-button"
                  style={{ width: 'auto', borderColor: '#795548', color: '#795548' }}
                  onClick={addStaffMember}
                >
                  Save Staff
                </button>
                <button
                  className="select-button"
                  style={{ width: 'auto', borderColor: '#795548', color: '#795548' }}
                  onClick={() => setShowAddStaff(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="divider"></div>

        {/* Target Beneficiaries */}
        <div className="form-section">
          <div className="section-title">
            Target Beneficiaries{' '}
            <span className="badge badge-count">
              {formData.beneficiaries.filter((b) => b.selected).length} selected
            </span>
          </div>
          <div className="section-subtitle">Select beneficiaries to invite for VHND</div>

          <div className="form-row">
            <div className="form-field">
              <input
                type="text"
                className="input-field"
                placeholder="Search beneficiaries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="form-field" style={{ flex: 0.5 }}>
              <select
                className="input-field"
                value={beneficiaryFilter}
                onChange={(e) => setBeneficiaryFilter(e.target.value)}
              >
                <option>All Categories</option>
                <option>Pregnant Woman</option>
                <option>Child</option>
                <option>Elderly</option>
              </select>
            </div>
          </div>

          <div className="beneficiary-list">
            {filteredBeneficiaries.map((beneficiary, index) => (
              <div className="beneficiary-item" key={index}>
                <input
                  type="checkbox"
                  className="beneficiary-checkbox"
                  checked={beneficiary.selected}
                  onChange={(e) =>
                    handleInputChange(e, 'beneficiaries', formData.beneficiaries.indexOf(beneficiary))
                  }
                />
                <div className="beneficiary-info">
                  <div className="beneficiary-name">
                    {beneficiary.name}
                    {beneficiary.category && (
                      <span
                        className={`beneficiary-type ${
                          beneficiary.category === 'Pregnant Woman'
                            ? 'type-pregnant'
                            : beneficiary.category === 'Child'
                            ? 'type-child'
                            : 'type-elderly'
                        }`}
                      >
                        {beneficiary.category}
                      </span>
                    )}
                  </div>
                  <div className="beneficiary-details">{beneficiary.details}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="form-row" style={{ marginTop: '16px' }}>
            <button
              className="select-button"
              style={{ marginBottom: 0, borderColor: '#795548', color: '#795548' }}
              onClick={selectAllBeneficiaries}
            >
              <span className="material-icons" style={{ marginRight: '8px' }}>
                people
              </span>
              Select All Beneficiaries
            </button>
            <button
              className="select-button"
              style={{ marginBottom: 0, borderColor: '#795548', color: '#795548' }}
            >
              <span className="material-icons" style={{ marginRight: '8px' }}>
                campaign
              </span>
              Send Reminders
            </button>
          </div>
        </div>

        <div className="divider"></div>

        {/* Services to be Provided */}
        <div className="form-section">
          <div className="section-title">Services to be Provided</div>
          <div className="section-subtitle">Select services planned for this VHND</div>

          <div className="service-grid">
            {formData.services.map((service, index) => (
              <div
                className={`service-card ${service.selected ? 'selected' : ''}`}
                key={index}
                onClick={() =>
                  handleInputChange(
                    { target: { type: 'checkbox', checked: !service.selected } },
                    'services',
                    index
                  )
                }
              >
                <div className="service-icon">
                  <span className="material-icons">
                    {service.title === 'Antenatal Care'
                      ? 'pregnant_woman'
                      : service.title === 'Immunization'
                      ? 'vaccines'
                      : service.title === 'Growth Monitoring'
                      ? 'child_care'
                      : service.title === 'Family Planning'
                      ? 'family_restroom'
                      : service.title === 'Geriatric Care'
                      ? 'elderly'
                      : 'medical_services'}
                  </span>
                </div>
                <div className="service-title">{service.title}</div>
                <div className="service-description">{service.description}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="divider"></div>

        {/* Logistics Checklist */}
        <div className="form-section">
          <div className="section-title">Logistics Checklist</div>
          <div className="section-subtitle">Ensure all necessary items are arranged</div>

          {formData.logistics.map((item, index) => (
            <div className="checklist-item" key={index}>
              <input
                type="checkbox"
                checked={item.checked}
                onChange={(e) => handleInputChange(e, 'logistics', index)}
              />
              <div className="checklist-content">
                <div className="checklist-title">{item.title}</div>
                <div className="checklist-description">{item.description}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="divider"></div>

        {/* Announcements & Outreach */}
        <div className="form-section">
          <div className="section-title">Announcements & Outreach</div>

          {[
            {
              name: 'villageAnnouncement',
              label: 'Village announcement arranged (Date: 14 Aug 2023)',
            },
            { name: 'smsReminders', label: 'SMS reminders to beneficiaries (Scheduled)' },
            { name: 'panchayatInformed', label: 'Panchayat members informed' },
            { name: 'bannersPosters', label: 'Banners and posters arranged' },
          ].map((item, index) => (
            <div className="checkbox-option" key={index}>
              <input
                type="checkbox"
                name={item.name}
                checked={formData.outreach[item.name]}
                onChange={(e) => handleInputChange(e, 'outreach')}
              />
              <label>{item.label}</label>
            </div>
          ))}

          <div className="form-field">
            <label className="label">Additional Outreach Notes</label>
            <textarea
              className="text-area"
              name="additionalNotes"
              value={formData.outreach.additionalNotes}
              onChange={(e) => handleInputChange(e, 'outreach')}
            />
          </div>
        </div>

        <div className="divider"></div>

        {/* Notes & Special Arrangements */}
        <div className="form-section">
          <div className="section-title">Notes & Special Arrangements</div>

          <div className="form-field">
            <textarea
              className="text-area"
              name="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange(e, 'root')}
            />
          </div>
        </div>

        <div className="divider"></div>

        {/* VHND Summary */}
        <div className="form-section">
          <div className="section-title">VHND Summary</div>

          <div className="summary-container">
            {[
              { label: 'Date & Time', value: `${formData.date}, ${formData.time}` },
              { label: 'Location', value: formData.location },
              { label: 'Focus Area', value: formData.focusArea },
              {
                label: 'Staff Confirmed',
                value: `${formData.staff.filter((s) => s.status === 'Confirmed').length}/${
                  formData.staff.length
                }`,
              },
              {
                label: 'Beneficiaries Invited',
                value: formData.beneficiaries.filter((b) => b.selected).length,
              },
              {
                label: 'Logistics Ready',
                value: `${formData.logistics.filter((l) => l.checked).length}/${
                  formData.logistics.length
                }`,
              },
            ].map((summary, index) => (
              <div className="summary-row" key={index}>
                <div className="summary-label">{summary.label}:</div>
                <div className="summary-value">{summary.value || 'N/A'}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="cancel-button">Cancel</button>
          <button className="save-button" onClick={handleSubmit} disabled={loading}>
            <span className="material-icons icon">save</span>
            {loading ? 'Submitting...' : 'Submit VHND Plan'}
          </button>
        </div>
      </div>
    </>
  );
};

export default VhndCreate;  